<script setup lang="ts">
import {
    CalendarClock,
    CircleAlert,
    ListChecks,
    Repeat2,
    Send,
} from '@lucide/vue';
import PlatformIcon from '@/components/marketing/shared/PlatformIcon.vue';
import SectionHeading from '@/components/marketing/shared/SectionHeading.vue';

const channels = [
    'instagram',
    'facebook',
    'youtube',
    'linkedin',
    'tiktok',
] as const;

const capabilities = [
    {
        icon: CalendarClock,
        text: 'Schedule a week or a month of content in one pass',
    },
    {
        icon: ListChecks,
        text: 'Follow every post from draft to published in one queue',
    },
    {
        icon: Repeat2,
        text: 'Retry a post that a platform rejected, without rewriting it',
    },
    {
        icon: Send,
        text: 'Check formats, sizes and captions before anything is sent',
    },
];

const pipeline = [
    'Draft',
    'Ready for review',
    'Approved',
    'Scheduled',
    'Publishing',
    'Published',
];

const sideStates = ['Failed', 'Cancelled'];
</script>

<template>
    <section id="publishing" class="scroll-mt-24 py-16 sm:py-20">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeading
                eyebrow="Publishing"
                title="Publish everywhere, on your schedule."
                description="Prepare one campaign and let your assistant adapt it for each channel, then send it when the time is right."
            />

            <div class="mt-10 flex flex-wrap justify-center gap-2">
                <PlatformIcon
                    v-for="channel in channels"
                    :key="channel"
                    :platform="channel"
                />
                <span
                    class="border-border text-muted-foreground inline-flex items-center rounded-full border border-dashed px-3 py-1.5 text-sm"
                >
                    More channels planned
                </span>
            </div>

            <div class="mt-12 grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
                <ul class="grid gap-3">
                    <li
                        v-for="item in capabilities"
                        :key="item.text"
                        class="flex items-start gap-3 text-sm sm:text-base"
                    >
                        <component
                            :is="item.icon"
                            class="text-primary mt-0.5 size-4 shrink-0"
                            aria-hidden="true"
                        />
                        {{ item.text }}
                    </li>
                </ul>

                <div class="bg-card border-border rounded-xl border p-6">
                    <p class="text-sm font-medium">Content pipeline</p>
                    <ol class="mt-4 flex flex-wrap items-center gap-2">
                        <li
                            v-for="(stage, index) in pipeline"
                            :key="stage"
                            class="flex items-center gap-2"
                        >
                            <span
                                class="border-border bg-background rounded-full border px-2.5 py-1 text-xs"
                            >
                                {{ stage }}
                            </span>
                            <span
                                v-if="index < pipeline.length - 1"
                                class="bg-border h-px w-3"
                                aria-hidden="true"
                            />
                        </li>
                    </ol>

                    <p class="text-muted-foreground mt-4 text-xs">
                        Side states:
                        <span
                            v-for="state in sideStates"
                            :key="state"
                            class="border-border bg-muted ml-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5"
                        >
                            <CircleAlert class="size-3" aria-hidden="true" />
                            {{ state }}
                        </span>
                    </p>
                </div>
            </div>

            <p class="text-muted-foreground mt-8 text-sm">
                What we can publish depends on each platform's own rules,
                permissions and account eligibility. We will only offer channels
                we can genuinely support.
            </p>
        </div>
    </section>
</template>
